import {
  GoogleAuthProvider,
  signInWithEmailAndPassword as fbSignInWithEmail,
  createUserWithEmailAndPassword as fbCreateUserWithEmail,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged as fbOnAuthStateChanged,
  type User as FirebaseUser,
  type UserCredential,
} from 'firebase/auth'
import { auth } from './client'

export type { FirebaseUser as User }

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  return fbSignInWithEmail(auth, email, password)
}

export const signUpWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  return fbCreateUserWithEmail(auth, email, password)
}

export const signInWithOAuth = async (
  provider: 'google' | 'github'
): Promise<UserCredential> => {
  if (provider === 'google') {
    const googleProvider = new GoogleAuthProvider()
    googleProvider.setCustomParameters({ prompt: 'select_account' })
    return signInWithPopup(auth, googleProvider)
  }
  throw new Error(`OAuth provider "${provider}" is not configured.`)
}

export const signOut = async (): Promise<void> => {
  await fbSignOut(auth)
  await fetch('/api/logout', { method: 'GET' }).catch(() => {})
}

export const getCurrentUser = (): FirebaseUser | null => {
  return auth.currentUser
}

export const onAuthStateChanged = (
  callback: (user: FirebaseUser | null) => void
) => {
  return fbOnAuthStateChanged(auth, callback)
}

export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken(forceRefresh)
}

/**
 * Establish a server-side session cookie after a successful client-side sign-in.
 * Call this immediately after signInWithEmail / signInWithOAuth returns.
 */
export const establishSession = async (): Promise<void> => {
  const idToken = await getIdToken(true)
  if (!idToken) throw new Error('No ID token available')
  const res = await fetch('/api/login', {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  if (!res.ok) {
    const errText = await res.text().catch(() => '')
    throw new Error(`Failed to establish session: ${errText}`)
  }
}
